import type { HRBootstrap, HRShift } from '../features/hr/hrData';

// Backs HR Center (/hr). All calls are scoped server-side to the signed-in user's
// agency; admin-only calls return 403 unless the user is the agency owner.

const hrBase = () => `${process.env.REACT_APP_Auth_Url}/api/HRCenter`;

function hrHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

// The API answers errors as { message }, { Message } or a plain string — surface whichever it sent.
async function hrRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${hrBase()}${path}`, {
    method,
    headers: hrHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const message = (data && typeof data === 'object' && (data.message || data.Message))
      || (typeof data === 'string' && data)
      || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data as T;
}

/** The browser's local date as yyyy-MM-dd (toISOString would give the UTC date). */
export function localDateKey(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export async function getHRBootstrap(today: string = localDateKey()): Promise<HRBootstrap> {
  const res = await hrRequest<{ success: boolean; data: HRBootstrap }>('GET', `/bootstrap?today=${encodeURIComponent(today)}`);
  return res.data;
}

export interface UpdateHREmployeePayload {
  title: string | null;
  startDate: string | null;
  birthday: string | null;
  ptoPlanId: number | null;
}

export async function updateHREmployee(id: number, payload: UpdateHREmployeePayload): Promise<void> {
  await hrRequest('PUT', `/employees/${id}`, payload);
}

/** Positive hours grant PTO; negative hours take it away. */
export async function allocatePto(employeeId: number, hours: number, note?: string): Promise<void> {
  await hrRequest('POST', `/employees/${employeeId}/pto-allocations`, { hours, note });
}

export interface CreateTimeOffPayload {
  ptoTypeId: number;
  startDate: string;
  endDate: string;
  hours: number;
  comments: string;
}

export async function createTimeOffRequest(payload: CreateTimeOffPayload): Promise<void> {
  await hrRequest('POST', '/time-off', payload);
}

export async function cancelTimeOffRequest(id: number): Promise<void> {
  await hrRequest('POST', `/time-off/${id}/cancel`);
}

export async function decideTimeOffRequest(id: number, decision: 'approved' | 'denied'): Promise<void> {
  await hrRequest('POST', `/time-off/${id}/${decision === 'approved' ? 'approve' : 'deny'}`);
}

export async function createPtoType(name: string, color: string, deductsBalance: boolean): Promise<void> {
  await hrRequest('POST', '/pto-types', { name, color, deductsBalance });
}

export async function deletePtoType(id: number): Promise<void> {
  await hrRequest('DELETE', `/pto-types/${id}`);
}

export async function createPtoPlan(name: string): Promise<void> {
  await hrRequest('POST', '/pto-plans', { name });
}

export async function deletePtoPlan(id: number): Promise<void> {
  await hrRequest('DELETE', `/pto-plans/${id}`);
}

export async function createPaidHoliday(date: string, name: string): Promise<void> {
  await hrRequest('POST', '/holidays', { date, name });
}

export async function deletePaidHoliday(id: number): Promise<void> {
  await hrRequest('DELETE', `/holidays/${id}`);
}

// ── Clock ──

/** Monday of the week containing `d`, as the browser's local yyyy-MM-dd. */
export function weekStartKey(d: Date = new Date()): string {
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
  return localDateKey(monday);
}

/** That week's shifts plus any still open — everyone's for the admin, your own otherwise. */
export async function getTimesheet(weekStart: string): Promise<HRShift[]> {
  const res = await hrRequest<{ success: boolean; data: HRShift[] }>('GET', `/timesheet?weekStart=${encodeURIComponent(weekStart)}`);
  return res.data;
}

export async function clockIn(workDate: string = localDateKey()): Promise<void> {
  await hrRequest('POST', '/clock/in', { workDate });
}

export interface MealBreakAnswer {
  mealBreakTaken: boolean;
  mealBreakNote?: string;
}

/** `meal` is the clock-out meal-break answer, when the person was asked. */
export async function clockOut(meal?: MealBreakAnswer): Promise<void> {
  await hrRequest('POST', '/clock/out', meal);
}

export async function startBreak(): Promise<void> {
  await hrRequest('POST', '/clock/break/start');
}

export async function endBreak(): Promise<void> {
  await hrRequest('POST', '/clock/break/end');
}

/** Admin only. Timestamps are ISO strings with an offset (toISOString()); a null clock-out leaves the shift open. */
export async function updateShift(id: number, clockInAt: string, clockOutAt: string | null): Promise<void> {
  await hrRequest('PUT', `/shifts/${id}`, { clockInAt, clockOutAt });
}

export async function deleteShift(id: number): Promise<void> {
  await hrRequest('DELETE', `/shifts/${id}`);
}

// ── Licenses ──

export interface SaveLicensePayload {
  state: string;
  licenseNumber: string;
  linesOfAuthority: string;
  expirationDate: string;
}

export async function createLicense(employeeId: number, payload: SaveLicensePayload): Promise<void> {
  await hrRequest('POST', `/employees/${employeeId}/licenses`, payload);
}

/** A new expiration date clears the license's Command link on the server. */
export async function updateLicense(id: number, payload: SaveLicensePayload): Promise<void> {
  await hrRequest('PUT', `/licenses/${id}`, payload);
}

export async function deleteLicense(id: number): Promise<void> {
  await hrRequest('DELETE', `/licenses/${id}`);
}

/** Records the Momentum Command task created as this license's renewal reminder. */
export async function setLicenseCommand(id: number, taskId: string): Promise<void> {
  await hrRequest('POST', `/licenses/${id}/command`, { taskId });
}

export async function saveHRSettings(licenseAlertDays: number): Promise<void> {
  await hrRequest('PUT', '/settings', { licenseAlertDays });
}

// ── HR events ──

export interface CreateHREventPayload {
  employeeId: number;
  typeLabel: string;
  eventDate: string;
  body: string;
  requestAcknowledgement: boolean;
  adminOnly: boolean;
}

export async function createHREvent(payload: CreateHREventPayload): Promise<void> {
  await hrRequest('POST', '/events', payload);
}

export async function deleteHREvent(id: number): Promise<void> {
  await hrRequest('DELETE', `/events/${id}`);
}

export async function acknowledgeHREvent(id: number): Promise<void> {
  await hrRequest('POST', `/events/${id}/acknowledge`);
}
